import { useState, useMemo, useEffect } from 'react'
import { useI18n } from './i18n/I18nContext'
import { LandingPage } from './components/LandingPage'
import { RepoCard } from './components/RepoCard'
import { SearchFilters } from './components/SearchFilters'
import { AdvancedFilters } from './components/AdvancedFilters'
import { StatsDashboard } from './components/StatsDashboard'
import { Pagination } from './components/Pagination'
import { LoadingSpinner } from './components/LoadingSpinner'
import { ErrorDisplay } from './components/ErrorDisplay'
import { GitHubTokenInput } from './components/GitHubTokenInput'
import { ConnectionStatus } from './components/ConnectionStatus'
import { Toast } from './components/Toast'
import { useRepos } from './hooks/useRepos'
import { useGitHubTokenAPI } from './hooks/useGitHubTokenAPI'
import { useFavorites } from './hooks/useFavorites'
import { getApiBaseUrl } from './lib/apiBaseUrl'
import './index.css'

const isDev = import.meta.env.DEV

export default function App() {
  const { t, locale, setLocale } = useI18n()
  const [showLanding, setShowLanding] = useState(() => {
    return localStorage.getItem('hasVisited') !== 'true'
  })
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(24)
  const [language, setLanguage] = useState<string>('')
  const [topic, setTopic] = useState<string>('')
  const [sort, setSort] = useState<'stars' | 'updated'>('stars')
  const [includePrivate, setIncludePrivate] = useState(false)
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false)
  const [showStats, setShowStats] = useState(true)
  
  // Filtres avancés
  const [minStars, setMinStars] = useState<number | null>(null)
  const [maxStars, setMaxStars] = useState<number | null>(null)
  const [minForks, setMinForks] = useState<number | null>(null)
  const [maxForks, setMaxForks] = useState<number | null>(null)
  const [dateRange, setDateRange] = useState<'all' | 'today' | 'week' | 'month' | 'year'>('all')
  const [hasHomepage, setHasHomepage] = useState<boolean | null>(null)
  const [isArchived, setIsArchived] = useState<boolean | null>(null)
  
  const [theme, setTheme] = useState<'light' | 'dark'>(
    (localStorage.getItem('theme') as 'light' | 'dark') || 'dark'
  )
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null)
  
  const { isFavorite, favoritesCount } = useFavorites()

  const { 
    token: githubToken, 
    setToken: setGitHubToken, 
    removeToken: removeGitHubToken,
    isValidating: isTokenValidating 
  } = useGitHubTokenAPI()

  const { data, loading, error, refetch } = useRepos({
    page,
    perPage,
    sort,
    language: language || undefined,
    topic: topic || undefined,
    includePrivate,
    search: search || undefined,
  })

  useEffect(() => {
    document.body.dataset.theme = theme
    localStorage.setItem('theme', theme)
  }, [theme])

  const handleGetStarted = () => {
    localStorage.setItem('hasVisited', 'true')
    setShowLanding(false)
  }

  const handleResetFilters = () => {
    setSearch('')
    setLanguage('')
    setTopic('')
    setIncludePrivate(false)
    setMinStars(null)
    setMaxStars(null)
    setMinForks(null)
    setMaxForks(null)
    setDateRange('all')
    setHasHomepage(null)
    setIsArchived(null)
    setPage(1)
  }

  const filtered = useMemo(() => {
    if (!data) return []
    
    let items = data.items
    
    // Filtrer par favoris si activé
    if (showFavoritesOnly) {
      items = items.filter(r => isFavorite(r.id))
    }
    
    // Filtrer par recherche textuelle
    const term = search.trim().toLowerCase()
    if (term) {
      items = items.filter(r =>
        r.name.toLowerCase().includes(term) ||
        (r.description || '').toLowerCase().includes(term) ||
        r.topics?.some(t => t.toLowerCase().includes(term))
      )
    }
    
    // Filtres avancés
    if (minStars !== null) {
      items = items.filter(r => r.stargazers_count >= minStars)
    }
    if (maxStars !== null) {
      items = items.filter(r => r.stargazers_count <= maxStars)
    }
    if (minForks !== null) {
      items = items.filter(r => r.forks_count >= minForks)
    }
    if (maxForks !== null) {
      items = items.filter(r => r.forks_count <= maxForks)
    }
    if (hasHomepage !== null) {
      items = items.filter(r => Boolean(r.homepage?.trim()) === hasHomepage)
    }
    if (isArchived !== null) {
      items = items.filter(r => r.archived === !isArchived)
    }
    
    // Filtre par date (nécessite les données de date du backend)
    // Pour l'instant, on filtre côté client si on a les données
    
    return items
  }, [search, data, showFavoritesOnly, isFavorite, minStars, maxStars, minForks, maxForks, hasHomepage, isArchived])

  const total = data?.total || 0
  const totalPages = Math.max(1, Math.ceil(total / perPage))

  if (showLanding) {
    return <LandingPage onGetStarted={handleGetStarted} />
  }

  return (
    <div className="container">
      <ConnectionStatus />
      
      <header className="app-header">
        <div className="brand">
          <span className="brand-icon">📦</span>
          <span className="brand-text">{t('nav.brand')}</span>
        </div>
        <div className="header-actions">
          <div className="locale-switch" role="group" aria-label={t('nav.aria.language')}>
            <button
              type="button"
              className={`btn-secondary small${locale === 'fr' ? ' active' : ''}`}
              onClick={() => setLocale('fr')}
              aria-pressed={locale === 'fr'}
            >
              {t('nav.locale.fr')}
            </button>
            <button
              type="button"
              className={`btn-secondary small${locale === 'en' ? ' active' : ''}`}
              onClick={() => setLocale('en')}
              aria-pressed={locale === 'en'}
            >
              {t('nav.locale.en')}
            </button>
          </div>
          {favoritesCount > 0 && (
            <button
              className={`btn-secondary small ${showFavoritesOnly ? 'active' : ''}`}
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              title={t('nav.favoritesTitle', { count: favoritesCount })}
            >
              ⭐ {t('nav.favorites')} ({favoritesCount})
            </button>
          )}
          <button
            className="theme-toggle-btn"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label={t('nav.themeToggle')}
          >
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
          <button
            className={`btn-secondary small ${showStats ? 'active' : ''}`}
            onClick={() => setShowStats(!showStats)}
            title={showStats ? t('nav.statsHide') : t('nav.statsShow')}
          >
            📊 {t('nav.stats')}
          </button>
          <button
            className="btn-secondary small"
            onClick={() => setShowLanding(true)}
            title={t('nav.home')}
          >
            🏠 {t('nav.home')}
          </button>
        </div>
      </header>

      <div className="settings-section">
        <GitHubTokenInput
          onTokenSet={async (token) => {
            try {
              const result = await setGitHubToken(token)
              setToast({
                message: result?.message || t('toast.tokenSaved'),
                type: 'success'
              })
              // Recharger les données après avoir défini le token
              setTimeout(() => refetch(), 500)
            } catch (err) {
              const errorMessage = err instanceof Error ? err.message : t('toast.tokenError')
              setToast({
                message: errorMessage,
                type: 'error'
              })
              throw err
            }
          }}
          onTokenRemove={async () => {
            try {
              await removeGitHubToken()
              setToast({
                message: t('toast.tokenRemoved'),
                type: 'info'
              })
              // Recharger les données après suppression du token
              setTimeout(() => refetch(), 500)
            } catch (err) {
              console.error('Error removing token:', err)
              setToast({
                message: t('toast.tokenRemoveError'),
                type: 'error'
              })
            }
          }}
          currentToken={githubToken || undefined}
          isLoading={isTokenValidating}
        />
      </div>

      <SearchFilters
        search={search}
        onSearchChange={setSearch}
        language={language}
        onLanguageChange={(value) => {
          setLanguage(value)
          setPage(1)
        }}
        topic={topic}
        onTopicChange={(value) => {
          setTopic(value)
          setPage(1)
        }}
        sort={sort}
        onSortChange={setSort}
        perPage={perPage}
        onPerPageChange={(value) => {
          setPerPage(value)
          setPage(1)
        }}
        includePrivate={includePrivate}
        onIncludePrivateChange={(value) => {
          setIncludePrivate(value)
          setPage(1)
        }}
        languages={data?.languages || []}
        topics={data?.topics || []}
        onResetFilters={handleResetFilters}
      />

      <AdvancedFilters
        minStars={minStars}
        setMinStars={setMinStars}
        maxStars={maxStars}
        setMaxStars={setMaxStars}
        minForks={minForks}
        setMinForks={setMinForks}
        maxForks={maxForks}
        setMaxForks={setMaxForks}
        dateRange={dateRange}
        setDateRange={setDateRange}
        hasHomepage={hasHomepage}
        setHasHomepage={setHasHomepage}
        isArchived={isArchived}
        setIsArchived={setIsArchived}
        onReset={handleResetFilters}
        theme={theme}
      />

      {showStats && filtered.length > 0 && (
        <StatsDashboard repos={filtered} totalRepos={data?.total || 0} />
      )}

      {loading && (
        <LoadingSpinner 
          size="large" 
          message={t('loading.repos')} 
        />
      )}

      {error && !loading && (
        <ErrorDisplay
          error={error}
          onRetry={refetch}
          onReload={() => window.location.reload()}
          showDebugInfo={isDev}
        />
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h3>{t('empty.noResults')}</h3>
          <p>
            {search || language || topic
              ? t('empty.adjustFilters')
              : t('empty.noneAvailable')}
          </p>
          {(search || language || topic) && (
            <button onClick={handleResetFilters} className="button">
              {t('empty.resetFilters')}
            </button>
          )}
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <>
          <div className="results-header">
            <span className="results-count">
              {filtered.length > 1
                ? t('results.countPlural', { n: filtered.length })
                : t('results.count', { n: filtered.length })}
              {total > filtered.length && t('results.ofTotal', { total: total.toLocaleString() })}
            </span>
          </div>
          <div className="grid">
            {filtered.map(repo => (
              <RepoCard key={repo.id} repo={repo} />
            ))}
          </div>
        </>
      )}

      {!loading && !error && total > 0 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          total={total}
          onPageChange={setPage}
          loading={loading}
        />
      )}

      {isDev && (
        <div className="footer">
          API: {getApiBaseUrl()} |{' '}
          Mode: {import.meta.env.MODE}
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  )
}
